import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Buildings, Plus, X, CircleNotch } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full text-xs border border-slate-300 px-2.5 py-2";
const slug = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const ASSET_TYPES = ["Pivotal RCT evidence", "Local clinical data", "Cost-effectiveness analysis", "Cost analysis",
  "Budget impact", "Real-world evidence", "Utilities / PROs", "Comparative effectiveness summary"];
const LEVELS = ["Mandatory", "Preferred", "Optional"];

export const OrganizationsPanel = ({ orgs, onChanged }) => {
  const [f, setF] = useState({ name: "", type: "MedTech", description: "" });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/organizations`, { ...f, id: slug(f.name) });
      toast.success(`Organization "${f.name}" created`);
      setF({ name: "", type: "MedTech", description: "" });
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create organization");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (org) => {
    if (!window.confirm(`Delete organization "${org.name}"? Workspaces and products under it will remain orphaned.`)) return;
    try {
      await axios.delete(`${API}/admin/organizations/${org.id}`);
      toast.success("Organization deleted");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };
  return (
    <section data-testid="organizations-panel" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200">
        <p className="font-heading font-bold text-sm tracking-tight">Organizations — Admin</p>
        <p className="text-xs text-slate-500 mt-1">Client organizations. Each owns workspaces and products.</p>
      </div>
      <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-5 gap-2">
        <input data-testid="org-name-input" className={`${inputCls} col-span-2`} placeholder="Organization name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <select data-testid="org-type-select" className={inputCls} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
          {["MedTech", "Pharma", "Biotech", "Consultancy"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <button data-testid="org-create-btn" disabled={!f.name.trim() || busy} onClick={create}
          className="col-span-2 bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-1.5">
          {busy ? <CircleNotch size={13} className="animate-spin" /> : <Plus size={13} weight="bold" />} Create
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {orgs.map((o) => (
          <div key={o.id} data-testid={`org-row-${o.id}`} className="px-5 py-2.5 flex items-center justify-between gap-2">
            <div className="min-w-0 flex items-center gap-2">
              <Buildings size={14} className="text-slate-300 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-800 truncate">{o.name}</p>
                <p className="text-[10px] text-slate-400">{o.id} · {o.type}</p>
              </div>
            </div>
            <button data-testid={`org-delete-${o.id}`} onClick={() => remove(o)} title="Delete organization"
              className="shrink-0 text-slate-300 hover:text-red-600 p-1">
              <X size={14} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export const AddMarketDialog = ({ onClose, onCreated }) => {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", code: "", flag: "🌐", region: "", hta_body: "", reimbursement_authority: "",
    pricing_mechanism: "", funding_pathway: "", healthcare_system: "", mai_readiness: 50,
    key_considerations: "",
  });
  const [profile, setProfile] = useState({});
  const [expectations, setExpectations] = useState([{ type: ASSET_TYPES[0], level: "Mandatory", detail: "" }]);

  useEffect(() => {
    axios.get(`${API}/regions`).then((r) => {
      setMeta(r.data);
      setProfile(Object.fromEntries(Object.keys(r.data.structural_dimensions).map((d) => [d, 50])));
      setF((prev) => ({ ...prev, region: r.data.regions[0]?.id || "" }));
    }).catch(console.error);
  }, []);

  const setExp = (i, key, val) => setExpectations((rows) => rows.map((r, j) => (j === i ? { ...r, [key]: val } : r)));
  const valid = f.name.trim() && f.code.trim() && f.region && f.hta_body.trim() && f.reimbursement_authority.trim()
    && f.pricing_mechanism.trim() && f.funding_pathway.trim() && f.healthcare_system.trim()
    && expectations.some((e) => e.detail.trim());

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/countries`, {
        ...f, id: slug(f.name), code: f.code.toUpperCase().slice(0, 3),
        mai_readiness: Number(f.mai_readiness) || 50,
        structural_profile: Object.fromEntries(Object.entries(profile).map(([k, v]) => [k, Number(v)])),
        evidence_expectations: expectations.filter((e) => e.detail.trim()),
      });
      toast.success(`${f.name} added — bucket "${r.data.detected_bucket}", initialized for ${r.data.products_initialized} product(s)`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create market");
    } finally {
      setBusy(false);
    }
  };

  if (!meta) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-market-dialog" className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-sm">Add Market to Global Registry</DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-500 -mt-1">The market master joins the Global Intelligence registry, is auto-classified into a structural bucket, and is initialized for every product workspace.</p>
        <div className="grid grid-cols-4 gap-2">
          <input data-testid="market-name-input" className={`${inputCls} col-span-2`} placeholder="Market name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="market-code-input" className={inputCls} placeholder="Code (e.g. BR) *" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
          <input className={inputCls} placeholder="Flag emoji" value={f.flag} onChange={(e) => setF({ ...f, flag: e.target.value })} />
          <select data-testid="market-region-select" className={`${inputCls} col-span-2`} value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })}>
            {meta.regions.map((r) => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
          </select>
          <input data-testid="market-hta-input" className={`${inputCls} col-span-2`} placeholder="HTA body (e.g. CONITEC) *" value={f.hta_body} onChange={(e) => setF({ ...f, hta_body: e.target.value })} />
          <input className={`${inputCls} col-span-2`} placeholder="Reimbursement authority *" value={f.reimbursement_authority} onChange={(e) => setF({ ...f, reimbursement_authority: e.target.value })} />
          <input className={`${inputCls} col-span-2`} placeholder="Pricing mechanism *" value={f.pricing_mechanism} onChange={(e) => setF({ ...f, pricing_mechanism: e.target.value })} />
          <input className={`${inputCls} col-span-2`} placeholder="Funding pathway *" value={f.funding_pathway} onChange={(e) => setF({ ...f, funding_pathway: e.target.value })} />
          <textarea className={`${inputCls} col-span-4 min-h-[52px]`} placeholder="Healthcare system summary *" value={f.healthcare_system} onChange={(e) => setF({ ...f, healthcare_system: e.target.value })} />
          <textarea className={`${inputCls} col-span-3 min-h-[40px]`} placeholder="Key considerations (optional)" value={f.key_considerations} onChange={(e) => setF({ ...f, key_considerations: e.target.value })} />
          <label className="text-[10px] text-slate-500">
            MAI readiness (0–100)
            <input type="number" min="0" max="100" className={inputCls} value={f.mai_readiness} onChange={(e) => setF({ ...f, mai_readiness: e.target.value })} />
          </label>
        </div>

        <p className="overline-label text-slate-400">Structural profile (0–100 per dimension — drives bucket classification)</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {Object.entries(meta.structural_dimensions).map(([dim, label]) => (
            <label key={dim} className="flex items-center gap-2 text-[11px] text-slate-600">
              <span className="flex-1 truncate" title={label}>{label}</span>
              <input data-testid={`dim-${dim}`} type="number" min="0" max="100" value={profile[dim]}
                onChange={(e) => setProfile({ ...profile, [dim]: e.target.value })}
                className="w-16 text-xs border border-slate-300 px-2 py-1 font-mono-data" />
            </label>
          ))}
        </div>

        <p className="overline-label text-slate-400">Evidence expectations (at least one)</p>
        <div className="space-y-1.5">
          {expectations.map((row, i) => (
            <div key={i} className="grid grid-cols-6 gap-1.5">
              <select className={`${inputCls} col-span-2`} value={row.type} onChange={(e) => setExp(i, "type", e.target.value)}>
                {ASSET_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <select className={inputCls} value={row.level} onChange={(e) => setExp(i, "level", e.target.value)}>
                {LEVELS.map((l) => <option key={l}>{l}</option>)}
              </select>
              <input data-testid={`exp-detail-${i}`} className={`${inputCls} col-span-2`} placeholder="Detail *" value={row.detail} onChange={(e) => setExp(i, "detail", e.target.value)} />
              <button onClick={() => setExpectations((rows) => rows.filter((_, j) => j !== i))} disabled={expectations.length === 1}
                className="text-slate-300 hover:text-red-600 disabled:opacity-30"><X size={14} /></button>
            </div>
          ))}
          <button data-testid="exp-add-row-btn" onClick={() => setExpectations((rows) => [...rows, { type: ASSET_TYPES[0], level: "Preferred", detail: "" }])}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1"><Plus size={12} /> Add expectation</button>
        </div>

        <button data-testid="market-submit-btn" disabled={!valid || busy} onClick={submit}
          className="w-full bg-slate-900 text-white text-xs font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><CircleNotch size={14} className="animate-spin" /> Creating market & rebuilding graphs…</> : "Add Market to Registry"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
